import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import ErrorTable from '../../bulk-stock-upload/ErrorTable';
import { useBulkStocktakeUpload } from '../useBulkStocktakeUpload';
import { ownerKey } from '../../../utils/ownerScopedSkuKey';
import { isJustInTimePair } from '../../../utils/stocktakeCsv';
import type { Product } from '../../../types';
import type { ValidationError } from '../useBulkStocktakeUpload';
import type { StocktakeCsvRow } from '../../../hooks/useCsvParser';

interface OrgSku {
  sku: string;
  skuCostOverride: number | null;
  id: string;
}

/** A parsed row that has passed every check, with its target org resolved —
 *  carried forward so `buildPreviewAndAdvance` (and later the modal) never
 *  has to re-resolve owner by bare sku. */
interface CountedRow extends StocktakeCsvRow {
  targetOrganizationId: string;
}

interface ValidationStepProps {
  products: Product[];
  allSkus: OrgSku[];
  /** Owner-cell resolution roster: lowercased/trimmed display name -> org id
   *  (own org + every connected client at this location). Built once by the
   *  modal from `useOrganization` + `useConnectedClients`. */
  ownerNameToOrgId: ReadonlyMap<string, string>;
  /** The caller's own org id — an empty Owner cell resolves to this. */
  ownOrgId: string;
}

const ValidationStep: React.FC<ValidationStepProps> = ({ products, allSkus, ownerNameToOrgId, ownOrgId }) => {
  const { t } = useTranslation('inventory');
  const {
    parsedRows,
    rawRows,
    sourceRows,
    validationErrors,
    setValidationErrors,
    setPreviewRows,
    setErroredRows,
    setNotCountedRows,
    setStep,
  } = useBulkStocktakeUpload();

  const [hasRun, setHasRun] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);

  // Rows that passed every check — built once, alongside `validationErrors`
  // and `notCountedRows`, in the same classification pass below. Kept in a
  // ref (not state) since it only feeds `buildPreviewAndAdvance`, never render.
  const countedRowsRef = useRef<CountedRow[]>([]);

  // Product lookup keyed by (owner org, sku) — NOT bare sku. At a shared 3PL
  // location, own-org and every connected client can stock the same sku
  // string; keying by bare sku collapses them into one row (last wins), which
  // corrupts both the existence check below and the currentStock used for the
  // preview diff. Legacy rows with no `ownerOrganizationId` stamp are treated
  // as own-org, matching `filterToTargetOrg`.
  const productMap = useMemo(
    () => new Map(products.map((p) => [ownerKey(p.ownerOrganizationId ?? ownOrgId, p.sku), p])),
    [products, ownOrgId]
  );

  // Every org id legitimately reachable at this location: the caller's own org
  // plus each connected client. An `Owner ID` outside this set is rejected
  // rather than trusted — the sheet must not be able to name an arbitrary org.
  const knownOrgIds = useMemo(
    () => new Set<string>([ownOrgId, ...ownerNameToOrgId.values()]),
    [ownOrgId, ownerNameToOrgId]
  );

  // Org-wide SKU lookup for cost data. Keyed by bare sku — safe here ONLY
  // because this list comes from listSkus.useQuery({}) with no
  // targetOrganizationId, which always returns the CALLER's own org's SKUs
  // (see BulkStocktakeWizardModal). It is read purely as a cost/id fallback,
  // never to decide stock existence or ownership, so a single-org result set
  // has no cross-org collision to guard against.
  const allSkuMap = useMemo(
    () => new Map(allSkus.map((s) => [s.sku, s])),
    [allSkus]
  );

  // Run client-side validation once on mount. Every row falls into EXACTLY
  // one bucket: error, not-counted (blank Stock Count), or counted.
  useEffect(() => {
    if (hasRun) return;
    setHasRun(true);

    const errors: ValidationError[] = [];
    const erroredRows: StocktakeCsvRow[] = [];
    const notCounted: StocktakeCsvRow[] = [];
    const counted: CountedRow[] = [];
    const countedKeys = new Set<string>();

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      if (!row) continue;
      const csvRow = sourceRows[i] ?? i + 2; // 1-indexed + header row fallback

      // Resolve the target org FIRST — every downstream decision (duplicate
      // check, existence check, cost lookup) must be scoped to the org that
      // owns this row, never the bare sku. A non-empty Owner cell that fails
      // to resolve is a hard per-row error: NEVER fall back to ownOrgId, that
      // would write a client's counted number onto the 3PL owner's stock row.
      //
      // Resolution order: Owner ID (a UUID) WINS over Owner (a display name).
      // Two organisations at one shared location can carry the same display
      // name, and the name column is editable by whoever holds the sheet, so
      // the id is the only unambiguous routing key. The name is kept for the
      // person counting the shelf.
      const ownerIdText = row.ownerId.trim();
      const ownerText = row.owner.trim();
      let rowOrgId: string;

      if (ownerIdText !== '') {
        if (!knownOrgIds.has(ownerIdText)) {
          errors.push({
            row: csvRow,
            sku: row.sku || '',
            reason: t('bulkStocktake.err.ownerIdMismatch', { line: csvRow }),
          });
          erroredRows.push(row);
          continue;
        }
        rowOrgId = ownerIdText;
      } else if (ownerText === '') {
        rowOrgId = ownOrgId;
      } else {
        const resolved = ownerNameToOrgId.get(ownerText.toLowerCase());
        if (!resolved) {
          errors.push({
            row: csvRow,
            sku: row.sku || '',
            reason: t('bulkStocktake.err.unknownOwner', { line: csvRow, owner: row.owner }),
          });
          erroredRows.push(row);
          continue;
        }
        rowOrgId = resolved;
      }

      if (!row.sku || row.sku.trim() === '') {
        errors.push({ row: csvRow, sku: row.sku || '', reason: t('bulkStocktake.err.missingSku', { line: csvRow }) });
        erroredRows.push(row);
        continue;
      }

      // Decide blank on the RAW cell text, never `Number.isNaN(row.stock)` —
      // both a blank cell AND junk like "n/a" parse to NaN, and conflating
      // them would silently count junk as "not counted".
      const rawStockText = (rawRows[i]?.stock ?? '').trim();
      if (rawStockText === '') {
        notCounted.push(row);
        continue;
      }

      if (Number.isNaN(row.stock)) {
        errors.push({
          row: csvRow,
          sku: row.sku,
          reason: t('bulkStocktake.err.notANumber', { line: csvRow, value: rawStockText }),
        });
        erroredRows.push(row);
        continue;
      }

      if (row.stock < 0) {
        errors.push({ row: csvRow, sku: row.sku, reason: t('bulkStocktake.err.negative', { line: csvRow }) });
        erroredRows.push(row);
        continue;
      }

      if (row.stock > 999999) {
        errors.push({
          row: csvRow,
          sku: row.sku,
          reason: t('bulkStocktake.err.tooLarge', { line: csvRow, value: rawStockText }),
        });
        erroredRows.push(row);
        continue;
      }

      const normalizedSku = row.sku.trim().toUpperCase();
      // rowOrgId is resolved to a real org id at this point — either ownOrgId
      // (blank Owner cell) or a match from ownerNameToOrgId (checked above).
      const compositeKey = ownerKey(rowOrgId, normalizedSku);

      if (countedKeys.has(compositeKey)) {
        errors.push({
          row: csvRow,
          sku: row.sku,
          reason: t('bulkStocktake.err.duplicateSku', { line: csvRow, sku: row.sku }),
        });
        erroredRows.push(row);
        continue;
      }

      // SKU must exist at this location, FOR THIS ORG, for stocktake — no auto-activation
      const product = productMap.get(compositeKey);
      if (!product) {
        errors.push({ row: csvRow, sku: row.sku, reason: t('stocktakeModal.notStockedAtLocation') });
        erroredRows.push(row);
        continue;
      }

      // BLU-1268: a Just in time pair has no shelf count (its stock figure is
      // the store constant), and the server refuses it. Said here, per row,
      // so the preview never shows the constant as a current count.
      if (isJustInTimePair(product)) {
        errors.push({ row: csvRow, sku: row.sku, reason: t('bulkStocktake.err.justInTime', { line: csvRow, sku: row.sku }) });
        erroredRows.push(row);
        continue;
      }

      countedKeys.add(compositeKey);
      counted.push({ ...row, targetOrganizationId: rowOrgId });
    }

    countedRowsRef.current = counted;
    setValidationErrors(errors);
    setErroredRows(erroredRows);
    setNotCountedRows(notCounted);

    // Auto-expand if few errors
    if (errors.length > 0 && errors.length < 10) {
      setErrorsOpen(true);
    }
  }, [hasRun, parsedRows, rawRows, sourceRows, productMap, ownerNameToOrgId, ownOrgId, setValidationErrors, setErroredRows, setNotCountedRows, t]);

  const buildPreviewAndAdvance = () => {
    const preview = countedRowsRef.current.map((row) => {
      const normalizedSku = row.sku.trim().toUpperCase();
      const product = productMap.get(ownerKey(row.targetOrganizationId, normalizedSku));
      const orgSku = allSkuMap.get(normalizedSku);
      const currentStock = product?.stockLevel ?? 0;
      const newStock = row.stock;
      const difference = newStock - currentStock;
      // Use location-level effectiveCost first, fall back to org-wide skuCostOverride
      const unitCost = product?.effectiveCost ?? orgSku?.skuCostOverride ?? null;
      const costImpact = unitCost != null ? unitCost * difference : null;

      return {
        sku: row.sku,
        skuId: product?.id ?? orgSku?.id ?? '',
        currentStock,
        newStock,
        difference,
        unitCost,
        costImpact,
        targetOrganizationId: row.targetOrganizationId,
      };
    });

    setPreviewRows(preview);
    setStep('preview');
  };

  // Auto-advance if no errors (with brief delay so user sees the success state)
  useEffect(() => {
    if (hasRun && validationErrors.length === 0 && countedRowsRef.current.length > 0) {
      const timer = setTimeout(buildPreviewAndAdvance, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRun, validationErrors.length]);

  const handleContinue = () => {
    buildPreviewAndAdvance();
  };

  const handleChangeFile = () => {
    setStep('upload');
    setValidationErrors([]);
  };

  // If still running validation, show nothing extra
  if (!hasRun) return null;

  const countedRowCount = countedRowsRef.current.length;

  // No errors — brief green flash before auto-advance
  if (validationErrors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <CheckCircle2 className="h-10 w-10 text-green-500" />
        <p className="text-sm font-medium text-green-700 dark:text-green-400">
          All {countedRowCount} rows valid
        </p>
        <p className="text-xs text-muted-foreground">Preparing preview...</p>
      </div>
    );
  }

  // Errors present
  return (
    <div className="space-y-4">
      {/* Summary */}
      <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertDescription className="text-amber-800 dark:text-amber-300">
          {t('bulkStocktake.errorsWillNotApply', { count: validationErrors.length })}{' '}
          <strong>{countedRowCount}</strong> valid row{countedRowCount !== 1 ? 's' : ''}{' '}
          will be applied.
        </AlertDescription>
      </Alert>

      {/* Collapsible error details */}
      <Collapsible open={errorsOpen} onOpenChange={setErrorsOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between px-3"
          >
            <span className="text-sm">
              {errorsOpen ? 'Hide' : 'Show'} {validationErrors.length} error
              {validationErrors.length !== 1 ? 's' : ''}
            </span>
            {errorsOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <ErrorTable errors={validationErrors} />
        </CollapsibleContent>
      </Collapsible>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={handleChangeFile}>
          Change File
        </Button>
        {countedRowCount > 0 && (
          <Button
            onClick={handleContinue}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Continue with {countedRowCount} valid rows
          </Button>
        )}
      </div>
    </div>
  );
};

export default ValidationStep;
